import { MsalGuardConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionRequiredAuthErrorCodes,
  InteractionType,
  LogLevel,
  PublicClientApplication,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { ConfigSettings } from '@shared/config';
import { SessionStorageService } from '@shared/services/session-storage.service';

export const MSAL_LOGIN_SCOPES = ['profile', 'openid', 'offline_access'];
export const POWER_BI_REPORT_READ_SCOPE =
  'https://analysis.windows.net/powerbi/api/Report.Read.All';

const MAX_REDIRECT_BOOT_ATTEMPTS = 3;

const INTERACTION_REQUIRED_ERROR_CODES = [
  InteractionRequiredAuthErrorCodes.interactionRequired,
  InteractionRequiredAuthErrorCodes.consentRequired,
  InteractionRequiredAuthErrorCodes.loginRequired,
  InteractionRequiredAuthErrorCodes.badToken,
  InteractionRequiredAuthErrorCodes.uiNotAllowed,
  InteractionRequiredAuthErrorCodes.interruptedUser,
];

const INTERACTION_REQUIRED_SUB_ERRORS = [
  'basic_action',
  'additional_action',
  'consent_required',
  'message_only',
  'user_password_expired',
  'bad_token',
  'ui_not_allowed',
  'interrupted_user',
];

const cleanRegex = /\/\*$/;

interface ProtectedResourceConfig {
  cleanUrl: string;
  useHomeTenant?: boolean;
  scopes: Array<string>;
}

let cachedRoutes: ProtectedResourceConfig[] | null = null;

export function msalInstanceFactory(
  settings: ConfigSettings
): IPublicClientApplication {
  // msal-browser v5 requires an absolute redirect URI; the configured value is
  // an origin-absolute path.
  const redirectUri = new URL(settings.msalConfig.redirectUri, document.baseURI)
    .href;

  return new PublicClientApplication({
    auth: {
      clientId: settings.msalConfig.clientId,
      authority: settings.msalConfig.authority,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  const scopes = [
    'user.read',
    'openid',
    'profile',
    'offline_access',
    POWER_BI_REPORT_READ_SCOPE,
  ];
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: scopes,
    },
  };
}

export function initializeMsalInstance(
  msal: IPublicClientApplication
): () => Promise<void> {
  return async () => {
    await msal.initialize();
    breakRedirectLoop();
  };
}

function breakRedirectLoop(): void {
  if (!hasPendingRedirectResponse()) {
    SessionStorageService.clearMsalRedirectBootAttempts();
    return;
  }

  const attempts = SessionStorageService.incrementMsalRedirectBootAttempts();
  if (attempts < MAX_REDIRECT_BOOT_ATTEMPTS) {
    return;
  }

  console.warn(
    `MSAL redirect response not processed after ${attempts} boots, aborting redirect flow`
  );
  SessionStorageService.clearMsalTemporaryCache();
  clearAuthResponseFromUrl();
  SessionStorageService.clearMsalRedirectBootAttempts();
  SessionStorageService.setMsalRedirectFailed(true);
}

function hasPendingRedirectResponse(): boolean {
  const hash = window.location.hash;
  const hashHasAuthResponse =
    hash.includes('state=') &&
    (hash.includes('code=') || hash.includes('error='));

  return (
    hashHasAuthResponse || SessionStorageService.hasMsalCachedRedirectResponse()
  );
}

function clearAuthResponseFromUrl(): void {
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search
  );
}

function initProtectedRoutes(settings: ConfigSettings) {
  const routes: ProtectedResourceConfig[] = [];

  const addProtectedResource = (
    url: string,
    scopes: Array<string>,
    useHomeTenant: boolean = false
  ) => {
    routes.push({
      cleanUrl: url.replace(cleanRegex, ''),
      useHomeTenant,
      scopes,
    });
  };

  addProtectedResource(
    'https://graph.microsoft.com/v1.0/me',
    ['User.Read'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/users',
    ['User.Read', 'User.ReadBasic.All'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/me/chats',
    ['Chat.Read', 'Chat.ReadWrite'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/chats',
    ['ChannelMessage.Send', 'Chat.ReadWrite', 'ChatMessage.Send'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/me/joinedTeams',
    ['Team.ReadBasic.All'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/teams',
    ['Team.ReadBasic.All', 'Channel.ReadBasic.All', 'ChannelMessage.Send'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/groups',
    ['Group.Read.All'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/me/people',
    ['People.Read'],
    true
  );
  addProtectedResource(
    'https://graph.microsoft.com/v1.0/me/sendMail',
    ['Mail.Send'],
    true
  );

  addProtectedResource('https://api.powerbi.com/v1.0/myorg/', [
    'https://analysis.windows.net/powerbi/api/.default',
  ]);

  addProtectedResource(`${settings.dataApiUrl}/data-retriever/*`, [
    settings.dataApiMsalScope,
  ]);

  addProtectedResource(`${settings.dataApiUrl}/connection/*`, [
    settings.dataApiMsalScope,
  ]);
  addProtectedResource(`${settings.dataApiUrl}/cache/*`, [
    settings.dataApiMsalScope,
  ]);

  return routes.sort((a, b) => b.cleanUrl.length - a.cleanUrl.length);
}

export const getProtectedRoutes = (
  settings: ConfigSettings
): ProtectedResourceConfig[] => {
  if (cachedRoutes) {
    return cachedRoutes;
  }

  cachedRoutes = initProtectedRoutes(settings);
  return cachedRoutes;
};

/**
 * Find the first route that matches more specific with the url that is passed.
 *
 * @param protectedRoutes
 * @param url
 */
export const findProtectedRouteMatch = (
  protectedRoutes: ProtectedResourceConfig[],
  url: string
) => {
  return protectedRoutes.find(route => {
    const { cleanUrl } = route;

    if (url.startsWith(cleanUrl)) {
      return (
        url.length === cleanUrl.length ||
        ['/', '?', '#'].includes(url.charAt(cleanUrl.length))
      );
    }

    return false;
  });
};

export const isInteractionRequiredError = (
  errorCode?: string,
  errorMessage?: string,
  subError?: string
): boolean => {
  const hasInteractionRequiredErrorCode =
    !!errorCode && INTERACTION_REQUIRED_ERROR_CODES.includes(errorCode);
  const hasInteractionRequiredSubError =
    !!subError && INTERACTION_REQUIRED_SUB_ERRORS.includes(subError);
  const hasInteractionRequiredMessage =
    !!errorMessage &&
    INTERACTION_REQUIRED_ERROR_CODES.some(code => errorMessage.includes(code));

  return (
    hasInteractionRequiredErrorCode ||
    hasInteractionRequiredSubError ||
    hasInteractionRequiredMessage
  );
};
